import { useQueries, useQuery } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysDeliveryNote } from './queryKeys';

/**
 * Fetches users who have access to a shared delivery note from the API.
 * @param {UUID} deliveryNoteId - The ID of the delivery note
 * @returns {Promise<UUID[]>} Array of user IDs who have access
 * @throws Error if the API request fails.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Delivery-Note/operation/get_list_of_users_that_asset_is_shared_with_asset__asset_id__share_get
 */
export const fetchSharedWithUsers = async (deliveryNoteId: UUID) => {
  const response = await vestigasApi
    .get(ENDPOINT.DELIVERY_NOTE.GET_SHARED_WITH_USERS(deliveryNoteId))
    .json<{ ids: UUID[] }>();

  return response?.ids ?? [];
};

const getSharedWithUsersQueryOptions = ({
  deliveryNoteId,
  options,
}: {
  deliveryNoteId: UUID;
  options?: Parameters<typeof useQuery>[0];
}) => ({
  queryFn: async () => fetchSharedWithUsers(deliveryNoteId),
  queryKey: queryKeysDeliveryNote.getSharedWithUsers(deliveryNoteId),
  ...options,
});

export const useQuerySharedWithUsersOfDln = (
  deliveryNoteId: UUID,
  options?: Parameters<typeof useQuery>[0],
) =>
  useQuery(
    getSharedWithUsersQueryOptions({
      deliveryNoteId,
      options,
    }),
  );

const combineUsers = (results) => {
  const isLoading = results.some(({ isLoading }) => isLoading);
  const isError = results.some(({ isError }) => isError);

  const sharedUserIds =
    isLoading || isError ? [] : results.flatMap(({ data }) => data || []);

  return {
    data: sharedUserIds,
    isError,
    isLoading,
  };
};

export const useQuerySharedWithUsersOfDlns = (deliveryNoteIds: UUID[] = []) =>
  useQueries({
    combine: (results) => combineUsers(results),
    queries: deliveryNoteIds.filter(Boolean).map((deliveryNoteId) =>
      getSharedWithUsersQueryOptions({
        deliveryNoteId,
        options: {},
      }),
    ),
  });
